import { useEffect, useLayoutEffect, useRef, useState, type RefObject, type ReactElement } from "react";
import { BODY_TYPE } from "odyssey-shared";
import { cutToFit } from "./fit.ts";
import { cssPerLogical, useCentred } from "./measure.ts";
import { KIT_METRICS, logical } from "./metrics.ts";

/**
 * The message line (brief section 6.3): a reply to what the player just did, in running text in the notice
 * frame, low on the screen over the world. It takes no pointer events, so a click on it reaches the world
 * (brief rule 14), and it is a polite live region, so assistive technology reads each new line without
 * interrupting. The region stays on the page while no line shows, since a live region added with its
 * text is not read.
 *
 * The frame is as wide as its text, rounded up to a whole logical pixel, and never wider than the line's
 * slot; a longer line wraps. It sits in the middle of the slot, rounded down to a whole logical pixel, so
 * its frame never lies between device pixels. It is moved there by a relative offset, which leaves the width
 * it lays out in the whole slot, so a line that replaces a shorter one, or none, paints at its final width
 * and place in its first frame. Given `maxLines`, a line that would wrap past that many lines is cut short
 * with U+2026 to the most its frame holds (P1.9-T13): a hidden copy of the frame, styled the same, is laid
 * out with each candidate, so the browser's own wrapping decides where the cut falls and the line drawn never
 * wraps further. How long a line stays and how a repeat restarts it belong to the surface that shows it.
 */

/**
 * Half a logical pixel, the slack a measured height is allowed over a whole number of lines.
 */
const HALF = 0.5;

/**
 * The text a message line shows: the text itself, or with `maxLines` the text cut to fit that many lines of
 * its frame, measured on the hidden copy. It is cut again when the text, the cap or the slot's width changes
 * (a new UI scale), and when a font finishes loading, since a face that arrives late wraps differently; the
 * body face is asked for as the line mounts, so the first reply is measured in it. Until a text is measured
 * it is shown whole, which a layout effect settles before the browser paints.
 *
 * @param area The line's region, whose width follows the slot's.
 * @param probe The hidden copy of the frame.
 * @param text The line, or null when none shows.
 * @param maxLines The most lines the frame may hold, or undefined for no cap.
 * @returns The text to show, or null.
 */
function useCut(area: RefObject<HTMLElement | null>, probe: RefObject<HTMLElement | null>, text: string | null, maxLines: number | undefined): string | null {
  const [cut, setCut] = useState<{ readonly text: string | null; readonly shown: string | null }>({ text: null, shown: null });
  const [layouts, setLayouts] = useState(0);
  useLayoutEffect(() => {
    const element = probe.current;
    if (maxLines === undefined || text === null || element === null) {
      return;
    }
    const px = cssPerLogical(element);
    if (px === null) {
      return;
    }
    const most = KIT_METRICS.message.height + (maxLines - 1) * BODY_TYPE.body.lineHeight;
    const shown = cutToFit(text, (candidate) => {
      element.textContent = candidate;
      return element.getBoundingClientRect().height / px <= most + HALF;
    });
    element.textContent = "";
    setCut((current) => (current.text === text && current.shown === shown ? current : { text, shown }));
  }, [probe, text, maxLines, layouts]);
  useEffect(() => {
    const region = area.current;
    const element = probe.current;
    if (maxLines === undefined || region === null || element === null) {
      return undefined;
    }
    let width = region.getBoundingClientRect().width;
    const observer = new ResizeObserver(() => {
      const next = region.getBoundingClientRect().width;
      if (next !== width) {
        width = next;
        setLayouts((count) => count + 1);
      }
    });
    observer.observe(region);
    const fonts = document.fonts;

    /**
     * Cuts the line again once a font has loaded.
     */
    const loaded = (): void => setLayouts((count) => count + 1);
    fonts.addEventListener("loadingdone", loaded);
    const style = getComputedStyle(element);
    void fonts.load(`${style.fontWeight} ${style.fontSize} ${style.fontFamily}`).catch(() => undefined);
    return () => {
      observer.disconnect();
      fonts.removeEventListener("loadingdone", loaded);
    };
  }, [area, probe, maxLines]);
  if (maxLines === undefined || text === null) {
    return text;
  }
  return cut.text === text ? cut.shown : text;
}

/**
 * A message line.
 *
 * @param props The line's `text`, or null (or empty) when none shows; the `width` of its slot in logical
 *   pixels, which is the widest the frame grows before the text wraps; and `maxLines`, the most lines the
 *   frame holds before a longer line is cut short with U+2026, or no cap when it is left out.
 * @returns The live region, holding the framed line when there is one.
 * @example
 * <MessageLine text={reply} width={384} maxLines={4} />
 */
export function MessageLine({ text, width, maxLines }: { readonly text: string | null; readonly width: number; readonly maxLines?: number }): ReactElement {
  const slot = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const probe = useRef<HTMLDivElement>(null);
  const shown = useCut(slot, probe, text !== null && text.length > 0 ? text : null, maxLines);
  const place = useCentred(slot, box, { x: true, y: false }, shown);
  const metrics = KIT_METRICS.message;
  const frame = { maxWidth: logical(width - metrics.chromeX), padding: `${logical(metrics.paddingY)} ${logical(metrics.paddingX)}` };
  return (
    <div ref={slot} className="ot-message" role="status" aria-live="polite" data-kit="message-line" style={{ width: logical(width) }}>
      {shown === null ? null : (
        <div ref={box} className="ot-message__box ot-ui-frame-notice" data-kit="message-box" style={{ ...frame, left: logical(place.left) }}>
          {shown}
        </div>
      )}
      {maxLines === undefined ? null : <div ref={probe} className="ot-message__box ot-message__probe ot-ui-frame-notice" aria-hidden="true" data-kit="message-probe" style={frame} />}
    </div>
  );
}
